import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { companyInfo } from '../mockData';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-[#DC2626]/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#DC2626]/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Company Info - OHNE LOGO */}
          <div className="md:col-span-2 space-y-6">
            <div className="text-3xl font-bold">
              <span className="bg-gradient-to-r from-[#DC2626] to-[#DC2626] bg-clip-text text-transparent">HK</span>
              <span className="text-white ml-2">Altbausanierung</span>
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md text-lg">
              Seit {companyInfo.founded} Ihr zuverlässiger Partner für professionelle Altbausanierung in Stuttgart und Umgebung. Qualität, Präzision und traditionelles Handwerk.
            </p>
            <div className="flex space-x-4">
              <a
                href={companyInfo.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626] to-[#DC2626] rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative w-12 h-12 rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-[#DC2626] hover:to-[#DC2626] backdrop-blur-md flex items-center justify-center transition-all duration-300 group-hover:scale-110 border border-white/20">
                  <Instagram className="w-5 h-5" />
                </div>
              </a>
              <a
                href={companyInfo.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626] to-[#DC2626] rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative w-12 h-12 rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-[#DC2626] hover:to-[#DC2626] backdrop-blur-md flex items-center justify-center transition-all duration-300 group-hover:scale-110 border border-white/20">
                  <Facebook className="w-5 h-5" />
                </div>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Navigation</h3>
            <ul className="space-y-4">
              {['hero', 'about', 'leistungen', 'projekte', 'kontakt'].map((section) => {
                const labels = {
                  hero: 'Startseite',
                  about: 'Über uns',
                  leistungen: 'Leistungen',
                  projekte: 'Projekte',
                  kontakt: 'Kontakt'
                };
                return (
                  <li key={section}>
                    <button
                      onClick={() => scrollToSection(section)}
                      className="text-gray-400 hover:text-[#DC2626] transition-all duration-300 hover:translate-x-1 inline-block"
                    >
                      {labels[section]}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Kontakt</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-gray-400 group">
                <MapPin className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-sm leading-relaxed">{companyInfo.address}</span>
              </li>
              <li className="flex items-start space-x-3 text-gray-400 group">
                <Phone className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                <div className="text-sm">
                  <a href={`tel:${companyInfo.phone}`} className="hover:text-[#DC2626] transition-colors block">
                    {companyInfo.phone} (Mobil)
                  </a>
                  <a href={`tel:${companyInfo.phoneLandline}`} className="hover:text-[#DC2626] transition-colors block">
                    {companyInfo.phoneLandline} (Festnetz)
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-gray-400 group">
                <Mail className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                <a href={`mailto:${companyInfo.email}`} className="text-sm hover:text-[#DC2626] transition-colors">
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm flex items-center space-x-2">
              <span>© {currentYear} HK Altbausanierung. Alle Rechte vorbehalten.</span>
              <span className="text-white/30">|</span>
              <Link 
                to="/impressum" 
                className="hover:text-[#DC2626] transition-colors duration-300"
                data-testid="footer-impressum-link"
              >
                Impressum
              </Link>
              <span className="text-white/30">|</span>
              <Link 
                to="/datenschutz" 
                className="hover:text-[#DC2626] transition-colors duration-300"
                data-testid="footer-datenschutz-link"
              >
                Datenschutz
              </Link>
            </p>
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span>Erstellt mit</span>
              <Heart className="w-4 h-4 text-[#DC2626] fill-current" />
              <span>in Stuttgart</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;